import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { JourneyTimeline } from './JourneyTimeline';
import { Mail, Smartphone, QrCode, Calendar, Globe, MessageSquare, Megaphone, Users, ExternalLink, Play } from 'lucide-react';
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    x: -20
  },
  show: {
    opacity: 1,
    x: 0
  }
};
const channels = [{
  icon: Globe,
  label: 'Embed',
  color: '#2D2926'
}, {
  icon: Smartphone,
  label: 'SMS',
  color: '#2D2926'
}, {
  icon: MessageSquare,
  label: 'WhatsApp',
  color: '#25D366'
}, {
  icon: QrCode,
  label: 'QR Code',
  color: '#2D2926'
}, {
  icon: Megaphone,
  label: 'Social',
  color: '#2D2926'
}, {
  icon: Mail,
  label: 'Email',
  color: '#E60012'
}, {
  icon: Play,
  label: 'Ads',
  color: '#2D2926'
}, {
  icon: Users,
  label: 'Congress',
  color: '#2D2926'
}];
const channelContainer = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.2
    }
  }
};
const channelItem = {
  hidden: {
    opacity: 0,
    scale: 0.8
  },
  show: {
    opacity: 1,
    scale: 1
  }
};
export function SlideBeforeCongress() {
  const [imgError, setImgError] = useState(false);
  return <div className="h-full w-full bg-[#FDF8F8] text-[#2D2926] p-6 md:p-10 flex flex-col" data-id="element-225">
      <JourneyTimeline currentStage={1} data-id="element-226" />

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-3 border-l-4 border-[#E60012] pl-5" data-id="element-227">
        <div className="flex items-center gap-3 mb-1" data-id="element-228">
          <span className="text-2xl" data-id="element-229">🎯</span>
          <h2 className="text-xl font-bold text-[#2D2926]" data-id="element-230">
            Stage 1: Pre-Congress — Agenda Setting
          </h2>
        </div>
        <p className="text-base text-[#E60012] font-medium" data-id="element-231">
          Goal: Create awareness & curiosity, set problems to explore at
          congress
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-5 min-h-0" data-id="element-232">
        {/* Left Column — Tactics, Features, Channels */}
        <div className="flex flex-col gap-3 overflow-auto" data-id="element-233">
          <motion.div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100" variants={container} initial="hidden" animate="show" data-id="element-234">
            <h3 className="text-sm font-bold text-[#2D2926] mb-3 flex items-center gap-2" data-id="element-235">
              <span className="w-6 h-1 bg-[#E60012] rounded-full" data-id="element-236"></span>
              Tactics
            </h3>
            <ul className="space-y-2.5" data-id="element-237">
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-238">
                <Mail className="w-4 h-4 text-[#E60012] mt-0.5 shrink-0" data-id="element-239" />
                <span className="text-gray-700 text-sm" data-id="element-240">
                  Email campaigns with rare disease topic teasers
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-241">
                <Smartphone className="w-4 h-4 text-[#E60012] mt-0.5 shrink-0" data-id="element-242" />
                <span className="text-gray-700 text-sm" data-id="element-243">
                  Snaks™ embedded in CSL portals & landing pages
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-244">
                <QrCode className="w-4 h-4 text-[#E60012] mt-0.5 shrink-0" data-id="element-245" />
                <span className="text-gray-700 text-sm" data-id="element-246">
                  QR codes on print & digital congress invites
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-247">
                <Calendar className="w-4 h-4 text-[#E60012] mt-0.5 shrink-0" data-id="element-248" />
                <span className="text-gray-700 text-sm" data-id="element-249">
                  Peer poll Snaks™ to spark curiosity & set agenda
                </span>
              </motion.li>
            </ul>
          </motion.div>

          <motion.div className="bg-[#E60012]/5 p-4 rounded-xl border border-[#E60012]/10" variants={container} initial="hidden" animate="show" data-id="element-250">
            <h3 className="text-sm font-bold text-[#2D2926] mb-3 flex items-center gap-2" data-id="element-251">
              <span className="w-6 h-1 bg-[#C5000F] rounded-full" data-id="element-252"></span>
              Snakker™ Features
            </h3>
            <ul className="space-y-2.5" data-id="element-253">
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-254">
                <div className="w-2 h-2 rounded-full bg-[#C5000F] mt-1.5 shrink-0" data-id="element-255" />
                <span className="text-gray-700 text-sm" data-id="element-256">
                  <strong className="text-[#2D2926]" data-id="element-257">Poll Cues™</strong> to
                  gauge peer opinions on rare disease topics
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-258">
                <div className="w-2 h-2 rounded-full bg-[#C5000F] mt-1.5 shrink-0" data-id="element-259" />
                <span className="text-gray-700 text-sm" data-id="element-260">
                  Personalized journeys using{' '}
                  <strong className="text-[#2D2926]" data-id="element-261">Splits</strong>
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-262">
                <div className="w-2 h-2 rounded-full bg-[#C5000F] mt-1.5 shrink-0" data-id="element-263" />
                <span className="text-gray-700 text-sm" data-id="element-264">
                  Calendar links & booth location CTAs
                </span>
              </motion.li>
            </ul>
          </motion.div>

          {/* Channel grid — compact row */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3" data-id="element-265">
            <h3 className="text-xs font-bold text-[#2D2926] mb-2 text-center" data-id="element-266">
              Deliver Snaks™ Anywhere
            </h3>
            <motion.div className="grid grid-cols-4 gap-1.5" variants={channelContainer} initial="hidden" animate="show" data-id="element-267">
              {channels.map((ch, i) => {
              const Icon = ch.icon;
              return <motion.div key={i} variants={channelItem} className="flex flex-col items-center gap-1 bg-[#FDF8F8] rounded-lg px-1 py-1.5" data-id="element-268">
                    <div className="w-6 h-6 rounded flex items-center justify-center" style={{
                  backgroundColor: `${ch.color}15`
                }} data-id="element-269">
                      <Icon className="w-3.5 h-3.5" style={{
                    color: ch.color
                  }} data-id="element-270" />
                    </div>
                    <span className="text-[9px] font-medium text-[#2D2926]" data-id="element-271">
                      {ch.label}
                    </span>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>

        {/* Right Column — CSL Poll Example */}
        <motion.div className="flex items-center justify-center min-h-0" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-272">
          <div className="relative w-full max-w-md" data-id="element-273">
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white" data-id="element-274">
              {imgError ? <div className="flex flex-col items-center justify-center w-full h-96 text-gray-400" data-id="element-275">
                  <Users className="w-12 h-12 mb-3 text-[#E60012]/40" data-id="element-276" />
                  <p className="text-sm font-medium" data-id="element-277">Image not available</p>
                </div> : <img src="https://cdn.magicpatterns.com/uploads/mM3VD4xQ8xTXdj5TvCmH3g/Screenshot_2026-04-01_at_09.02.47.png" alt="CSL ERA quiz — When do you escalate beyond RAAS blockade?" className="w-full h-auto object-contain" onError={() => setImgError(true)} data-id="element-278" />}
            </div>
            <p className="mt-3 text-xs text-[#E60012] italic text-center" data-id="element-279">
              Example: CSL/ERA quiz on RAAS blockade escalation — sparks
              curiosity & sets agenda for booth discussion
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}